import { BalanceData, Fix64, OfferItem, OfferPrice, RARITY_IDS, Rotation, SECONDS_PER_DAY, chestShare, isRandomItem } from "@highmarch/domain";
import type { OfferView, PageView } from "@highmarch/app";

/**
 * The Deals screen's view models (docs/SHOP.md §5.2): pure functions from the app's `PageView`/`OfferView` to what
 * the screen draws — the tab strip, countdowns, item tiles with their numbers at the player's level, price, value and
 * "was" strings, limit lines and the button text a refusal becomes. No DOM, no clock (`now` is passed in), no rules:
 * what is on sale, what it pays and what it is worth are @highmarch/app `Deals`'s answers; this only words them.
 */

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const CENTS_PER_DOLLAR = 100;
/** Compact numbers switch to "K" from here (a tile is ~60 px wide: "12,500" does not fit, "12.5K" does). */
const COMPACT_FROM = 10_000;
const THOUSAND = 1000;
const MILLION = 1_000_000;

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** A real countdown: "1d 12:37:10", "12:37:10", "04:05" (never negative). */
export function fmtCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / SECONDS_PER_DAY), h = Math.floor((s % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
  const m = Math.floor((s % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE), sec = s % SECONDS_PER_MINUTE;
  const hms = `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
  return d > 0 ? `${d}d ${hms}` : h > 0 ? hms : `${pad2(m)}:${pad2(sec)}`;
}

/** The countdown to a page's real reset, or null for once/none pages (no timer: nothing is rushed that never ends). */
export function countdownText(endsAt: number | null, now: number): string | null {
  return endsAt === null ? null : fmtCountdown(endsAt - now);
}

/** Tile numbers: 950, 9,500, 12.5K, 1.2M. */
export function fmtAmount(n: number): string {
  if (n >= MILLION) return `${trimDec(n / MILLION)}M`;
  if (n >= COMPACT_FROM) return `${trimDec(n / THOUSAND)}K`;
  return Math.round(n).toLocaleString("en-US");
}
const trimDec = (v: number): string => (v >= 100 ? String(Math.floor(v)) : String(Math.floor(v * 10) / 10));

/** A speed-up's length as Kingshot writes it on the tile: "5m", "1h", "8h", "1d". */
export function fmtMinutes(minutes: number): string {
  if (minutes >= HOURS_PER_DAY * MINUTES_PER_HOUR && minutes % (HOURS_PER_DAY * MINUTES_PER_HOUR) === 0) return `${minutes / (HOURS_PER_DAY * MINUTES_PER_HOUR)}d`;
  const h = Math.floor(minutes / MINUTES_PER_HOUR), m = minutes % MINUTES_PER_HOUR;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ------------------------------------------------------------------ tabs

export interface TabView {
  readonly id: string;
  readonly title: string;
  /** The tab's label: the title, or a short name that fits two lines of a 76 px tab at 375 px ("Master's"). */
  readonly label: string;
  /** The tab's painted icon, `art/interface/icons/deals_<id>.png`. */
  readonly icon: string;
  /** Things to act on now on this page (a reached Weekly Deal milestone): the red dot. Never an offer. */
  readonly dot: number;
}

/** Tab labels shorter than the page's title, so each fits two lines of its tab at 375 px. */
const SHORT_TAB: Readonly<Record<string, string>> = { welcome: "Welcome", masters: "Master's", commander: "Commander", chapter: "Chapter", armoury: "Armoury", treasury: "Treasury" };

/** A page's tab label: its short name, else its title. */
export const tabLabel = (id: string, title: string): string => SHORT_TAB[id] ?? title;

/** The strip, in the order `Deals.pages` gives (catalog order; the gems page last). */
export function tabsFor(pages: readonly PageView[]): TabView[] {
  return pages.map((p) => ({ id: p.page.id, title: p.page.title, label: tabLabel(p.page.id, p.page.title), icon: `icons/deals_${p.page.id}.png`, dot: owedMilestones(p) }));
}

/** Reached, unclaimed milestones on a page's track (0 without one). */
export function owedMilestones(p: PageView): number {
  return p.track ? p.track.milestones.filter((m) => m.reached && !m.claimed).length : 0;
}

/** Which tab opens: the one asked for when it is on the strip, else the first (First Command or Daily Deals). */
export function initialTab(tabs: readonly TabView[], want: string | null): string | null {
  if (want && tabs.some((t) => t.id === want)) return want;
  return tabs[0]?.id ?? null;
}

/** The hero illustration (`art/interface/deals/page_<id>.webp`, a still life per page, tools/art/kit/heroes_bpy.py); a
 * Chapter page shows its chapter's own (the chapter's colours on the banner, one prop of its own). */
export function pageArt(p: PageView): string {
  if (p.page.id === "chapter") {
    const n = p.offers[0]?.offerId.split(".")[1];
    if (n) return `deals/page_chapter_${n}.webp`;
  }
  return `deals/page_${p.page.id}.webp`;
}

/**
 * The hero's blurb: the page's own (offers.json), except where the m7 layout moved what it points at — the Weekly
 * token track now sits under the hero, not above the offers.
 */
const BLURB: Readonly<Record<string, string>> = { weekly: "Every pack pays a Weekly Token. Tokens open free rewards." };
export const pageBlurb = (page: { readonly id: string; readonly blurb: string }): string => BLURB[page.id] ?? page.blurb;

/** The line under the offers: how often this page's limits come back (the countdown gives the exact time). */
export function resetLine(rotation: Rotation): string {
  switch (rotation) {
    case "daily": return "Fresh deals every day";
    case "weekly": return "Limits reset each week";
    case "fortnight": return "A new collection every two weeks";
    case "once": return "Once only · it waits for you";
    case "none": return "";
  }
}

// ------------------------------------------------------------------ items

/** A tile's colour: a rarity id, or the kind's own (gems, time, supplies, token). */
export type TileTone = (typeof RARITY_IDS)[number] | "gem" | "time" | "supply" | "skin";

export interface ItemTile {
  readonly kind: OfferItem["kind"];
  /** Art path: `hq:icons/chest_rare.png` (art/hq) or `ui:icons/supplies.png` (art/interface). */
  readonly icon: string;
  /** The small band over the icon (a speed-up's length). */
  readonly top: string;
  /** The number in the corner: an amount (gems, resources) or "×n". */
  readonly count: string;
  readonly tone: TileTone;
  readonly name: string;
  /** The info line a tap shows ("2,060 gold at your level"). */
  readonly detail: string;
  /** Holds a random roll: odds are one tap away. */
  readonly random: boolean;
  /** A gear pick whose slot is not chosen yet (the tile wears a "?"). */
  readonly needsSlot: boolean;
  /**
   * The rarity colour its square wears (0 common … 5 mythic; shop redesign 2026-10-19): chests and gear by their
   * rarity, gems, gold and speed-ups by their size, so value reads at a glance (Kingshot's tiles).
   */
  readonly tier?: number;
  /** The item it shows: a tap brings it into focus with its odds (owner 2026-10-21). */
  readonly item?: OfferItem;
}

/** Shop redesign: a currency's or a speed-up's square colour by size (see ItemTile.tier). */
export function amountTier(kind: "gems" | "supplies" | "speedUp", n: number): number {
  switch (kind) {
    case "gems": return n <= 250 ? 2 : n <= 1000 ? 3 : n < 10000 ? 4 : 5;
    case "supplies": return n < 2 ? 1 : n < 6 ? 2 : n < 15 ? 3 : n < 60 ? 4 : 5;
    case "speedUp": return n < 60 ? 1 : n < 240 ? 2 : n < 1440 ? 3 : 4;
  }
}

export interface TileNames {
  /** A skin's display name ("Ivory"); default: the id's last part, capitalised. */
  skin?(id: string): string;
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const rarityName = (r: number): string => cap(RARITY_IDS[r] ?? "common");
const skinDefault = (id: string): string => cap(id.split(".").pop() ?? id);
const skinKind = (id: string): string => id.split(".")[0] ?? "";

/** Supplies (average Win chests of gold) as the gold they pay at `level`. */
export function suppliesAt(balance: BalanceData, level: number, chests: Fix64): number {
  return chestShare(balance, Math.max(1, level), chests);
}

/**
 * One tile per item. `gearSlot` is the chosen slot of a `pick` gear item (−1 or undefined while unchosen); gear of a
 * random slot shows the rarity's mount with its odds one tap away.
 */
export function itemTile(balance: BalanceData, item: OfferItem, level: number, gearSlot = -1, names: TileNames = {}): ItemTile {
  const random = isRandomItem(item);
  const base = { kind: item.kind, random, needsSlot: false, top: "", item };
  const times = (n: number): string => (n > 1 ? `×${n}` : "");
  switch (item.kind) {
    case "gems":
      return { ...base, icon: "hq:icons/res_gems.png", count: fmtAmount(item.amount), tone: "gem", tier: amountTier("gems", item.amount), name: `${item.amount.toLocaleString("en-US")} gems`, detail: "Spend on speed-ups, crates, the VIP shop and the Black Market" };
    case "supplies": {
      const gold = suppliesAt(balance, level, item.chests);
      return { ...base, icon: "hq:icons/res_gold.png", count: fmtAmount(gold), tone: "supply", tier: amountTier("supplies", item.chests.toNumber()), name: "Gold",
        detail: `${gold.toLocaleString("en-US")} gold at your level, for towers and gear` };
    }
    case "speedUp":
      return { ...base, icon: "hq:icons/res_speedup.png", top: fmtMinutes(item.minutes), count: times(item.count), tone: "time", tier: amountTier("speedUp", item.minutes),
        name: `${fmtMinutes(item.minutes)} speed-up${item.count > 1 ? ` ×${item.count}` : ""}`, detail: "Cuts any upgrade short. Keeps till you need it" };
    case "chest": {
      const r = RARITY_IDS[item.rarity] ?? "common";
      return { ...base, icon: `hq:icons/chest_${r}.png`, count: times(item.count), tone: r, name: `${rarityName(item.rarity)} chest${item.count > 1 ? ` ×${item.count}` : ""}`,
        detail: "Gear, gold and a speed-up at your level" };
    }
    case "luckyChest": {
      const def = balance.offers.luckyChests.get(item.table);
      const royal = item.table.includes("royal");
      return { ...base, icon: `ui:icons/chest_${royal ? "royal" : "lucky"}.png`, count: times(item.count), tone: royal ? "mythic" : "epic",
        name: `${def?.name ?? "Lucky Chest"}${item.count > 1 ? ` ×${item.count}` : ""}`, detail: "Its rarity is decided as it opens" };
    }
    case "gear": {
      const r = RARITY_IDS[item.rarity] ?? "common";
      const slot = item.slot === "pick" && gearSlot >= 0 ? balance.gear.slots[gearSlot] : undefined;
      const icon = `ui:gear/gear_${slot?.id ?? "mount"}_${r}.png`;
      const what = item.slot === "any" ? "a random slot" : slot ? `${slot.name} slot` : "you pick the slot";
      return { ...base, icon, count: times(item.count), tone: r, needsSlot: item.slot === "pick" && !slot,
        name: `${rarityName(item.rarity)} ${slot ? slot.name : "gear"}${item.count > 1 ? ` ×${item.count}` : ""}`, detail: `Bound to you, at your level · ${what}` };
    }
    case "skin": {
      const name = (names.skin ?? skinDefault)(item.skinId);
      return { ...base, icon: `ui:skins/${item.skinId.replace(".", "_")}.png`, count: "", tone: "skin", tier: 4, name: `${name} ${skinKind(item.skinId)} skin`, detail: "Looks only, pure glory. Owned already? Its gems come back" };
    }
  }
}

/**
 * What a Buy All or a pack lists, merged for the tiles: gems add up, supplies add up, speed-ups of one
 * length and chests of one rarity count together. Gear stays one tile per item (each has its own slot).
 */
export function mergeItems(items: readonly OfferItem[]): OfferItem[] {
  const out: OfferItem[] = [];
  for (const it of items) {
    const i = out.findIndex((o) => sameStack(o, it));
    if (i < 0) { out.push(it); continue; }
    const o = out[i];
    if (o.kind === "gems" && it.kind === "gems") out[i] = { ...o, amount: o.amount + it.amount };
    else if (o.kind === "supplies" && it.kind === "supplies") out[i] = { ...o, chests: o.chests.add(it.chests) };
    else if ("count" in o && o.kind !== "gems" && o.kind !== "supplies" && o.kind !== "skin") out[i] = { ...o, count: o.count + it.count } as OfferItem;
  }
  return out;
}

function sameStack(a: OfferItem, b: OfferItem): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "gems": case "supplies": return true;
    case "speedUp": return b.kind === "speedUp" && a.minutes === b.minutes;
    case "chest": return b.kind === "chest" && a.rarity === b.rarity;
    case "luckyChest": return b.kind === "luckyChest" && a.table === b.table;
    case "gear": case "skin": return false;
  }
}

/**
 * The tiles for an offer's items, gems first (the headline number, as Kingshot leads with it), gear picks carrying
 * the chosen slot in order.
 */
export function itemTiles(balance: BalanceData, items: readonly OfferItem[], level: number, gearSlots: readonly number[] = [], names: TileNames = {}): ItemTile[] {
  let g = 0;
  const tiles = mergeItems(items).flatMap((it) => {
    if (it.kind === "gear" && it.slot === "pick") {
      // one tile per picked item: each has its own slot choice
      return Array.from({ length: it.count }, () => itemTile(balance, { ...it, count: 1 }, level, gearSlots[g++] ?? -1, names));
    }
    return [itemTile(balance, it, level, -1, names)];
  });
  return [...tiles.filter((t) => t.kind === "gems"), ...tiles.filter((t) => t.kind !== "gems")];
}

/** The offer's gems (the big number on a hero pack or a Daily tier), 0 when none. */
export function gemsOf(items: readonly OfferItem[]): number {
  return items.reduce((n, it) => n + (it.kind === "gems" ? it.amount : 0), 0);
}

// ------------------------------------------------------------------ prices, value, limits

/** The store's own price for the product (StoreKit localises), else the reference tier (the sandbox). */
export function priceText(price: OfferPrice, storePrices: ReadonlyMap<string, string>): string {
  return storePrices.get(price.product) ?? price.label;
}

/** "$5.97" from cents: the reference currency (the sandbox store and the catalog are US tiers). */
export const centsText = (cents: number): string => `$${(cents / CENTS_PER_DOLLAR).toFixed(2)}`;

/**
 * Buy All's struck-through price. It is honest only as the genuine sum of the three tiers: with store prices, the
 * sum of their local prices when all three share one format ("£0.99", "1,99 €"); otherwise the reference sum.
 * Null when the local prices cannot be summed (then no "was" price is shown rather than a made-up one).
 */
export function wasText(wasCents: number | null, tierPrices: readonly OfferPrice[], storePrices: ReadonlyMap<string, string>): string | null {
  if (wasCents === null) return null;
  const local = tierPrices.map((p) => storePrices.get(p.product));
  if (local.every((l) => l === undefined)) return centsText(wasCents);
  if (local.some((l) => l === undefined)) return null;
  return sumPriceLabels(local as string[]);
}

/** Sums store price labels of one currency and format ("£0.99" + "£1.99" → "£2.98"); null if they differ in shape. */
export function sumPriceLabels(labels: readonly string[]): string | null {
  const NUM = /(\d{1,3}(?:[.,   ]\d{3})*(?:[.,]\d{2})|\d+(?:[.,]\d{2})?)/;
  let shape: string | null = null, decimalComma = false, total = 0;
  for (const l of labels) {
    const m = NUM.exec(l);
    if (!m) return null;
    const s = l.replace(m[0], "#");
    if (shape !== null && s !== shape) return null;
    shape = s;
    const raw = m[0];
    const dec = /[.,](\d{2})$/.exec(raw);
    decimalComma = dec !== null && raw[raw.length - 3] === ",";
    const whole = (dec ? raw.slice(0, -3) : raw).replace(/[^\d]/g, "");
    total += Number(whole) * CENTS_PER_DOLLAR + (dec ? Number(dec[1]) : 0);
  }
  if (shape === null) return null;
  const whole = Math.floor(total / CENTS_PER_DOLLAR), cents = pad2(total % CENTS_PER_DOLLAR);
  return shape.replace("#", `${whole}${decimalComma ? "," : "."}${cents}`);
}

/** The starburst: "621%". Only drawn when `showValue` (the app's rule: from 110 %). */
export const valueText = (percent: number): string => `${Math.floor(percent)}%`;

/** The VIP bubble on the price: "+500". */
export const vipText = (points: number): string => `+${points.toLocaleString("en-US")}`;

/** "Remaining: 2" under a limited offer; "" for an unlimited one (gem packs). */
export function remainingText(v: OfferView): string {
  if (!Number.isFinite(v.remaining)) return "";
  return `${Math.max(0, v.remaining)} left`;
}

/** The refusal codes (@highmarch/app Deals `blocked`/`prepare`) as the button says them. */
export function blockedText(code: string): string {
  const WORDS: Record<string, string> = {
    "sold out": "Sold out",
    "choose your picks": "Fill the chest",
    "choose a prize": "Pick a prize",
    "choose a gear slot": "Pick a slot",
    "buy all needs none bought today": "A tier was bought today",
    "not saving here": "Open in another tab",
    "not here": "Buy from the hub",
    "not on sale": "No longer on sale",
    "not ready": "Already claimed today",
    "not reached": "Not reached yet",
    "already claimed": "Already claimed",
    "already owned": "Already yours",
    "not owned": "Buy it first",
    "not open": "Not open yet",
    "card running": "Running · renew in its last days",
    "purchases are turned off on this device": "Purchases are switched off on this iPhone",
  };
  return WORDS[code] ?? "Not right now";
}

/** A store purchase that did not complete, as a toast: the reason in plain words when it is one of ours. */
export function purchaseFailText(reason: string): string {
  const said = blockedText(reason);
  return said === "Not right now" ? "The purchase didn't go through. Try again in a moment" : `The purchase didn't go through: ${said.toLowerCase()}`;
}

/** Whether a blocked offer is waiting on the player's own choice (the button then opens the chooser). */
export const needsChoice = (code: string): boolean => code === "choose your picks" || code === "choose a prize" || code === "choose a gear slot";

/** The offers a Daily Deals page lists as tiers (daily.1..3, in order) and its Buy All. */
export function dailySplit(p: PageView): { tiers: OfferView[]; buyAll: OfferView | null } {
  return { tiers: p.offers.filter((o) => o.offerId !== "daily.all"), buyAll: p.offers.find((o) => o.offerId === "daily.all") ?? null };
}

/**
 * The Weekly track as the bar draws it: milestones evenly spaced along the bar (Kingshot's), each with its state.
 * `at` is the stop's place on the bar (0..1).
 */
export function trackStops(track: NonNullable<PageView["track"]>): { tokens: number; at: number; state: "claimed" | "ready" | "locked"; items: readonly OfferItem[] }[] {
  const n = track.milestones.length;
  return track.milestones.map((m, i) => ({ tokens: m.tokens, at: (i + 1) / n, state: m.claimed ? "claimed" : m.reached ? "ready" : "locked", items: m.items }));
}

/** The bar's fill (0..1) on that even spacing: full to each reached stop, part-way to the next by the tokens between. */
export function trackFill(track: NonNullable<PageView["track"]>): number {
  const ms = track.milestones, n = ms.length;
  if (n === 0) return 0;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const at = ms[i].tokens;
    if (track.tokens < at) return (i + (track.tokens - prev) / Math.max(1, at - prev)) / n;
    prev = at;
  }
  return 1;
}
